import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Button,
  Paper,
  Popper,
  ClickAwayListener,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  TextField,
  Divider,
  Badge,
  Chip,
  InputAdornment,
  Fade,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import DateRangePicker from "react-daterange-picker";
import "react-calendar/dist/Calendar.css";
import FilterListIcon from "@mui/icons-material/FilterList";
import DateRangeOutlined from "@mui/icons-material/DateRangeOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import moment from "moment";
import axios from "axios";
import { getSecureItem } from "../../utilsFunctions";
import { BaseUrl } from "../../Environment";

// ─── Constants ────────────────────────────────────────────────────────────────

const DATE_FILTER_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const CATEGORY_META = [
  {
    key: "branches",
    label: "Branches",
    icon: <AccountBalanceIcon fontSize="small" />,
    color: "#f59e0b",
    bgColor: "#fffbeb",
  },
  {
    key: "agents",
    label: "Agents",
    icon: <SupportAgentIcon fontSize="small" />,
    color: "#10b981",
    bgColor: "#ecfdf5",
  },
  {
    key: "dates",
    label: "Dates",
    icon: <DateRangeOutlined fontSize="small" />,
    color: "#6366f1",
    bgColor: "#eef2ff",
  },
];

const DEFAULT_SELECTED = {
  dates: [],
  branches: [],
  agents: [],
  dateFilterType: ["today"],
};

const DEFAULT_DATE_RANGE = moment.range(
  moment().clone().subtract(7, "days"),
  moment().clone(),
);

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FilterBtn({ onFilterChange }) {
  const loggedinUser = getSecureItem("loggedinUser");

  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("branches");
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [selected, setSelected] = useState(DEFAULT_SELECTED);
  const [branchData, setBranchData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [dateFilterType, setDateFilterType] = useState("today");

  const anchorRef = useRef(null);

  // Enrich static category meta with dynamic items
  const FILTER_CONFIG = CATEGORY_META.map((cat) => ({
    ...cat,
    items:
      cat.key === "branches"
        ? branchData
        : cat.key === "agents"
          ? userData
          : [],
  }));

  const isCustom = selected?.dateFilterType?.includes("custom");

  const totalSelected = Object.entries(selected)
    .filter(([key]) => {
      if (isCustom) {
        // exclude dateFilterType when custom
        return key !== "dateFilterType";
      }
      return true;
    })
    .reduce((sum, [, value]) => {
      return sum + (Array.isArray(value) ? value.length : 0);
    }, 0);

  const activeCategoryConfig = FILTER_CONFIG.find(
    (f) => f.key === activeCategory,
  );

  const filteredItems = (activeCategoryConfig?.items ?? []).filter((item) =>
    item?.label?.toLowerCase().includes(searchText.toLowerCase()),
  );

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleToggle = () => {
    setOpen((prev) => {
      if (!prev) setActiveCategory("branches");
      return !prev;
    });
  };

  const handleClose = () => setOpen(false);

  const handleCategoryHover = (key) => {
    setActiveCategory(key);
    setSearchText("");
  };

  const onSelect = (range) => {
    const { start, end } = range ?? {};
    setDateRange(range);

    const updatedDates =
      start && end
        ? [
            {
              startDate: moment(start).format("YYYY-MM-DD"),
              endDate: moment(end).format("YYYY-MM-DD"),
            },
          ]
        : [];

    const updated = {
      ...selected,
      dates: updatedDates,
      dateFilterType: ["custom"],
    };
    setDateFilterType("custom");
    setSelected(updated);
    onFilterChange(updated);
  };

  const handleItemToggle = (categoryKey, item) => {
    setSelected((prev) => {
      const current = prev[categoryKey];
      const isSelected = current.some((i) => i.value === item.value);

      const updated =
        categoryKey === "dates"
          ? isSelected
            ? current.filter((i) => i.value !== item.value)
            : [...current, item]
          : isSelected
            ? []
            : [item]; // single-select for branches & agents

      const next = { ...prev, [categoryKey]: updated };
      onFilterChange(next);
      return next;
    });
  };

  const handleReset = () => {
    setSelected(DEFAULT_SELECTED);
    onFilterChange(DEFAULT_SELECTED);
    setDateFilterType("today");
  };

  // ─── Data Fetching ───────────────────────────────────────────────────────────

  const fetchBranches = useCallback(async () => {
    try {
      const token = getSecureItem("token");
      const { data } = await axios.get(`${BaseUrl}/branch/getAllBranches`, {
        params: { companyId: loggedinUser?.companyId },
        headers: { Authorization: `Bearer ${token}` },
      });

      const branchData =
        loggedinUser?.userType === "superAdmin"
          ? data?.data
          : data?.data?.filter(
              (item) => item?.branchId === loggedinUser?.branchId,
            );

      const formattedBranches = [
        ...(loggedinUser?.userType === "superAdmin"
          ? [
              {
                label: "Clear (X)",
                value: null,
              },
            ]
          : []),
        ...(branchData?.map((item) => ({
          label: item.branchName,
          value: item.branchId,
        })) || []),
      ];

      setBranchData(formattedBranches);
    } catch (err) {
      console.error("Fetch Branches Error:", err);
    }
  }, [loggedinUser?.companyId, loggedinUser?.branchId, loggedinUser?.userType]);

  const fetchUsers = useCallback(async () => {
    try {
      const token = getSecureItem("token");
      const { data } = await axios.get(
        `${BaseUrl}/user/getAllUsers?userName=&companyId=${loggedinUser?.companyId}&branchId=${loggedinUser?.branchId || ""}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const users =
        loggedinUser?.userType === "Collection Agent"
          ? data?.result?.filter(
              (item) => item?.userId === loggedinUser?.userId,
            )
          : data?.result;

      setUserData(
        users?.map((item) => ({
          label: `${item.firstName} ${item.lastName}`,
          value: item?.userId,
        })) ?? [],
      );
    } catch (err) {
      console.error("Fetch Users Error:", err);
    }
  }, [loggedinUser?.companyId, loggedinUser?.userId, loggedinUser?.userType]);

  useEffect(() => {
    fetchBranches();
    fetchUsers();
  }, [fetchBranches, fetchUsers]);

  // ─── Render ──────────────────────────────────────────────────────────────────

  const isDateCategory = activeCategory === "dates";

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: "relative", display: "inline-block" }}>
        {/* Filter Button */}
        <Badge
          badgeContent={totalSelected}
          color="error"
          overlap="circular"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 10,
              fontWeight: 700,
              minWidth: 18,
              height: 18,
            },
          }}
        >
          <Button
            ref={anchorRef}
            variant="outlined"
            onClick={handleToggle}
            startIcon={<FilterListIcon />}
            className="fonts"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              px: 2,
              py: 1,
              borderColor: open ? "#6366f1" : "#d1d5db",
              color: open ? "#6366f1" : "#374151",
              bgcolor: open ? "#eef2ff" : "#fff",
              "&:hover": {
                borderColor: "#6366f1",
                bgcolor: "#f5f3ff",
                color: "#6366f1",
              },
            }}
          >
            Filters
          </Button>
        </Badge>

        {/* Dropdown Popper */}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          transition
          style={{ zIndex: 1300 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={160}>
              <Paper
                elevation={8}
                sx={{
                  mt: 1,
                  borderRadius: "14px",
                  overflow: "hidden",
                  display: "flex",
                  border: "1px solid #e5e7eb",
                  boxShadow:
                    "0 12px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
                  minWidth: isDateCategory ? 550 : 420,
                  maxHeight: isDateCategory ? 400 : 300,
                }}
              >
                {/* Left Panel: Categories */}
                <Box
                  sx={{ width: 200, borderRight: "1px solid #f3f4f6", py: 1 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 0.75,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      className="Fonts"
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: 0.6,
                      }}
                    >
                      Filter by
                    </Typography>
                    {totalSelected > 0 && (
                      <Button
                        size="small"
                        startIcon={
                          <RestartAltIcon
                            sx={{ fontSize: "14px !important" }}
                          />
                        }
                        onClick={handleReset}
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#6366f1",
                          textTransform: "none",
                          p: 0,
                          minWidth: 0,
                          "&:hover": {
                            bgcolor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Reset
                      </Button>
                    )}
                  </Box>

                  <List disablePadding>
                    {FILTER_CONFIG.map((cat) => {
                      const count = selected[cat.key].length;
                      const isActive = activeCategory === cat.key;
                      return (
                        <ListItemButton
                          key={cat.key}
                          onMouseEnter={() => handleCategoryHover(cat.key)}
                          selected={isActive}
                          sx={{
                            px: 2,
                            py: 1,
                            mx: 0.5,
                            borderRadius: "8px",
                            "&.Mui-selected": {
                              bgcolor: "#f5f3ff",
                              "&:hover": { bgcolor: "#f5f3ff" },
                            },
                            "&:hover": { bgcolor: "#f9fafb" },
                          }}
                          className="Fonts"
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: "8px",
                                bgcolor: cat.bgColor,
                                color: cat.color,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {cat.icon}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={cat.label}
                            className="Fonts"
                            primaryTypographyProps={{
                              fontSize: 14,
                              fontWeight: isActive ? 600 : 500,
                              color: isActive ? "#6366f1" : "#1f2937",
                            }}
                          />
                          {count > 0 && (
                            <Chip
                              label={count}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: "#6366f1",
                                color: "#fff",
                                "& .MuiChip-label": { px: 0.8 },
                                mr: 0.5,
                              }}
                            />
                          )}
                          <ChevronRightIcon
                            sx={{ fontSize: 16, color: "#d1d5db" }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Box>

                {/* Right Panel: Sub-items */}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                    <Typography
                      className="Fonts"
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: 0.6,
                        mb: 1,
                      }}
                    >
                      {activeCategoryConfig?.label}
                    </Typography>
                    {!isDateCategory && (
                      <TextField
                        size="small"
                        placeholder="Search..."
                        value={searchText}
                        className="Fonts"
                        onChange={(e) => setSearchText(e.target.value)}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{ fontSize: 16, color: "#9ca3af" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: 13,
                            bgcolor: "#f9fafb",
                            "& fieldset": { borderColor: "#e5e7eb" },
                            "&:hover fieldset": { borderColor: "#6366f1" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#6366f1",
                            },
                          },
                        }}
                      />
                    )}
                  </Box>

                  <Divider sx={{ mt: 1, borderColor: "#f3f4f6" }} />

                  {isDateCategory ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        p: 2,
                        gap: 2,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ToggleButtonGroup
                        value={dateFilterType}
                        exclusive
                        onChange={(_, v) => {
                          const updated = {
                            ...selected,
                            dates: [],
                            dateFilterType: [v],
                          };
                          setSelected(updated);
                          onFilterChange(updated);
                          v && setDateFilterType(v);
                        }}
                        size="small"
                        sx={{
                          bgcolor: "#f3f4f6",
                          borderRadius: 3,
                          "& .MuiToggleButton-root": {
                            border: "none",
                            borderRadius: "12px !important",
                            px: 2,
                            py: 0.5,
                            fontWeight: 600,
                            fontSize: 12,
                            color: "#6b7280",
                            "&.Mui-selected": {
                              bgcolor: "#fff",
                              color: "#1f2937",
                              boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                            },
                          },
                        }}
                      >
                        {DATE_FILTER_OPTIONS.map(({ value, label }) => (
                          <ToggleButton key={value} value={value}>
                            {label}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                      <DateRangePicker
                        value={dateRange}
                        onSelect={onSelect}
                        singleDateRange
                      />
                    </Box>
                  ) : (
                    <List
                      disablePadding
                      sx={{ overflow: "auto", maxHeight: 260, py: 0.5 }}
                    >
                      {filteredItems.length === 0 ? (
                        <Typography
                          className="Fonts"
                          sx={{
                            fontSize: 13,
                            color: "#9ca3af",
                            textAlign: "center",
                            py: 3,
                          }}
                        >
                          No results found
                        </Typography>
                      ) : (
                        filteredItems.map((item) => {
                          const isChecked = selected[activeCategory].some(
                            (s) => s.value === item.value,
                          );
                          return (
                            <ListItemButton
                              key={item.value}
                              dense
                              onClick={() =>
                                handleItemToggle(activeCategory, item)
                              }
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "6px",
                                mx: 0.5,
                                "&:hover": { bgcolor: "#f5f3ff" },
                              }}
                            >
                              <Checkbox
                                checked={isChecked}
                                size="small"
                                sx={{
                                  p: 0.5,
                                  color: "#d1d5db",
                                  "&.Mui-checked": { color: "#6366f1" },
                                }}
                                disableRipple
                              />
                              <ListItemText
                                primary={item.label}
                                className="Fonts"
                                primaryTypographyProps={{
                                  fontSize: 13.5,
                                  fontWeight: isChecked ? 600 : 400,
                                  color: isChecked ? "#4f46e5" : "#374151",
                                  ml: 0.5,
                                }}
                              />
                            </ListItemButton>
                          );
                        })
                      )}
                    </List>
                  )}

                  {selected[activeCategory].length > 0 && (
                    <>
                      <Divider sx={{ borderColor: "#f3f4f6" }} />
                      <Box sx={{ px: 2, py: 1 }}>
                        <Typography
                          className="Fonts"
                          sx={{
                            fontSize: 12,
                            color: "#6366f1",
                            fontWeight: 600,
                          }}
                        >
                          {selected[activeCategory].length} selected
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
